import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'

import { Typography } from 'components/Typography/Typography'
import { ProtocolOption, SelectProtocolMenu } from './SelectProtocolMenu'
import { Collateral } from './Collateral'
import { PriceRange } from './PriceRange'
import { Leverage } from './Leverage'
import { useIsMobile } from 'utils/useIsMobile'
import { NewPositionBreakdown } from './NewPositionBreakdown'
import { usePositionBalances } from './hooks/usePositionBalances'
import { useFocusWithin } from 'utils/useFocusWithin'
import { PositionConfigInfo } from '@kunalabs-io/kai'
import { PairInfo } from 'utils/pair-info'
import { usePio } from '@/shared/kai/hooks/usePio'
import { IconClose } from 'components/Icons/IconClose'
import LiquidityChartRangeInput from 'components/LiquidityChart'
import { Bound } from 'components/LiquidityChart/types'
import { useLpSizeUsdValue } from './hooks/useLpSizeUsdValue'
import { useConfigData } from './hooks/useConfigData'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { usePriceRange } from './hooks/usePriceRange'
import { useLeverageRange } from './hooks/useLeverageRange'
import { usePositionCreationValues } from './hooks/usePositionCreationValues'
import { useCreatePosition } from './hooks/useCreatePosition'

const NewPositionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: auto;
  margin-top: 24px;
  align-items: stretch;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    // padding: 0 20px;
  }
`

const LeftContainer = styled.div`
  width: 75%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`
const RightContainer = styled.div`
  width: 25%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`

const ContentContainer = styled.div`
  padding: 16px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
`

const Gap = styled.div`
  height: 40px;
`

const LEVERAGE_STEP = 0.01

interface Props {
  pairInfo: PairInfo<string>
  onClose: () => void
  onCreatePositionSuccess: () => void
  onCreatePositionProcessed?: (newPositionId?: string) => void | Promise<void>
  initialConfigId?: string | null
  initialMaxPrice?: string | null
  initialMinPrice?: string | null
  ref: React.Ref<HTMLDivElement>
}

export const NewPosition = ({
  onCreatePositionSuccess,
  onCreatePositionProcessed,
  onClose,
  pairInfo,
  initialConfigId,
  initialMaxPrice,
  initialMinPrice,
  ref,
}: Props) => {
  // --- Config selection state ---
  const [configInfo, setConfigInfo] = useState<
    PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  >(() => {
    if (!initialConfigId) {
      return pairInfo.configInfos[0]
    }
    return pairInfo.configInfos.find(c => c.configId === initialConfigId) || pairInfo.configInfos[0]
  })

  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  // --- On-chain data fetching ---
  const { config, pool, supplyPoolX, supplyPoolY } = useConfigData(configInfo)

  // --- Collateral balances ---
  const {
    xInfo,
    sanitizedXValue,
    xCollateralError,
    xCollateralDollars,
    handleXChange,
    handleMaxClickX,
    handleHalfClickX,
    yInfo,
    sanitizedYValue,
    yCollateralError,
    yCollateralDollars,
    handleYChange,
    handleMaxClickY,
    handleHalfClickY,
    sanitizedXOnBlur,
    sanitizedYOnBlur,
    handleSetXOnBlur,
    handleSetYOnBlur,
    handleReverseInputValues,
  } = usePositionBalances(configInfo)

  const { pioX, pioY } = usePio(configInfo)

  // --- Price range ---
  const {
    tickA,
    tickB,
    priceRangeDisplayProps,
    currentPriceDisplay,
    displaySymbolX,
    displaySymbolY,
    handleTickAChange,
    handleTickBChange,
    resetTicks,
  } = usePriceRange({
    configInfo,
    pool,
    initialConfigId,
    initialMinPrice,
    initialMaxPrice,
  })

  // --- Leverage range ---
  const { leverage, handleLeverageChange, startL, maxL, maxLeverage, leverageSliderMarks } = useLeverageRange({
    config,
    pool,
    pioX,
    pioY,
    sanitizedXOnBlur,
    sanitizedYOnBlur,
    tickA,
    tickB,
    isConnected,
  })

  // --- Position creation values ---
  const { positionCreationValues, notEnoughSupply } = usePositionCreationValues({
    pool,
    config,
    configInfo,
    supplyPoolX,
    supplyPoolY,
    tickA,
    tickB,
    leverage,
    startL,
    maxL,
    maxLeverage,
    sanitizedXOnBlur,
    sanitizedYOnBlur,
  })

  const lpSizeUsdValue = useLpSizeUsdValue(configInfo, positionCreationValues)

  // --- Transaction handling ---
  const { onCreatePosition, isCreatePositionLoading } = useCreatePosition({
    config,
    configInfo,
    positionCreationValues,
    lpSizeUsdValue,
    leverage,
    maxLeverage,
    rangeMin: priceRangeDisplayProps.rangeMin,
    rangeMax: priceRangeDisplayProps.rangeMax,
    sanitizedXValue,
    sanitizedYValue,
    onSuccess: onCreatePositionSuccess,
    onProcessed: onCreatePositionProcessed,
  })

  // --- Protocol selection ---
  const onSelectConfig = useCallback(
    async (value: ProtocolOption) => {
      const newConfigInfo = pairInfo.configInfos.find(c => c.configId === value.id)
      if (!newConfigInfo) {
        throw new Error('config not found')
      }

      if (newConfigInfo.isReversedPair !== configInfo.isReversedPair) {
        handleReverseInputValues()
      }

      if (newConfigInfo.configId !== configInfo.configId) {
        setConfigInfo(newConfigInfo)
        resetTicks()
      }
    },
    [pairInfo.configInfos, handleReverseInputValues, configInfo, resetTicks]
  )

  // --- Close on disconnect ---
  const isCopiedPosition = Boolean(initialConfigId && initialMinPrice && initialMaxPrice)
  useEffect(() => {
    if (!isConnected && !isCopiedPosition) {
      onClose()
    }
  }, [isConnected, isCopiedPosition, onClose])

  // --- Collateral focus-within refs ---
  const { ref: collateralXRef } = useFocusWithin({
    onBlur: () => {
      handleSetXOnBlur(sanitizedXValue)
    },
  })

  const { ref: collateralYRef } = useFocusWithin({
    onBlur: () => {
      handleSetYOnBlur(sanitizedYValue)
    },
  })

  // --- Derived state ---
  const isMobile = useIsMobile()
  const formDisabled = !configInfo || !isConnected

  const createPositionDisabled =
    !!notEnoughSupply ||
    xCollateralError ||
    yCollateralError ||
    !positionCreationValues ||
    !positionCreationValues.liquidity

  const protocolOptions = useMemo(
    () =>
      pairInfo.configInfos.map(config => ({
        id: config.configId,
        name: config.name,
        img: config.isCetus() ? '/cetus.png' : '/bluefin.png',
      })),
    [pairInfo.configInfos]
  )

  const activeProtocolOption = configInfo
    ? { id: configInfo.configId, name: configInfo.name, img: configInfo.isCetus() ? '/cetus.png' : '/bluefin.png' }
    : null

  // --- Render ---
  return (
    <NewPositionContainer style={{ marginTop: 24 }} ref={ref}>
      <LeftContainer>
        <ContentContainer>
          <Typography
            variant={isMobile ? 'description' : 'label1'}
            fontWeight="semiBold"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            New position
            <div style={{ marginLeft: 'auto', display: 'flex', cursor: 'pointer' }} onClick={onClose}>
              <IconClose />
            </div>
          </Typography>
          <Gap />
          <SelectProtocolMenu
            options={protocolOptions}
            activeOption={activeProtocolOption}
            onOptionClick={onSelectConfig}
          />
          <Collateral
            disabled={formDisabled}
            xIcon={configInfo?.X.iconUrl}
            xSymbol={configInfo?.X.displaySymbol || ''}
            xBalance={xInfo}
            xError={xCollateralError}
            handleMaxClickX={handleMaxClickX}
            handleHalfClickX={handleHalfClickX}
            xDepositValue={sanitizedXValue}
            handleXChange={handleXChange}
            yIcon={configInfo?.Y.iconUrl}
            ySymbol={configInfo?.Y.displaySymbol || ''}
            yBalance={yInfo}
            yError={yCollateralError}
            handleMaxClickY={handleMaxClickY}
            handleHalfClickY={handleHalfClickY}
            yDepositValue={sanitizedYValue}
            handleYChange={handleYChange}
            isReversedPair={configInfo.isReversedPair}
            xCollateralDollars={xCollateralDollars}
            yCollateralDollars={yCollateralDollars}
            collateralXRef={collateralXRef}
            collateralYRef={collateralYRef}
          />
          <PriceRange
            currentPrice={currentPriceDisplay}
            tokenXSymbol={displaySymbolX}
            tokenYSymbol={displaySymbolY}
            disabled={formDisabled}
            {...priceRangeDisplayProps}
          />
          {pool && (
            <div style={{ marginTop: 48 }}>
              <LiquidityChartRangeInput
                pool={pool}
                reverseTokens={configInfo.isReversedPair}
                ticksAtLimit={{ [Bound.LOWER]: false, [Bound.UPPER]: false }}
                tickA={tickA}
                tickB={tickB}
                onTickAChange={handleTickAChange}
                onTickBChange={handleTickBChange}
                interactive={true}
              />
            </div>
          )}
          <Leverage
            leverage={leverage}
            handleLeverageChange={handleLeverageChange}
            labels={leverageSliderMarks.map(l => l.value)}
            disabled={maxLeverage === undefined}
            max={maxLeverage?.toNumber()}
            step={LEVERAGE_STEP}
          />
        </ContentContainer>
      </LeftContainer>
      <RightContainer>
        {supplyPoolX && supplyPoolY && (
          <NewPositionBreakdown
            supplyPoolX={supplyPoolX}
            supplyPoolY={supplyPoolY}
            onSubmit={onCreatePosition}
            submitDisabled={createPositionDisabled}
            positionCreationValues={positionCreationValues}
            configInfo={configInfo}
            leverage={leverage}
            notEnoughSupply={notEnoughSupply}
            isLoading={isCreatePositionLoading}
            isReversedPair={configInfo.isReversedPair}
          />
        )}
      </RightContainer>
    </NewPositionContainer>
  )
}
